import {
  LEGAL_BODY_HTML_FIELD_KEY,
  bindingPlaceholderToken,
  bindingRefDisplayToken,
  isFieldRoleBound,
  isPdlMissingValue,
  legalFieldLabelContentKey,
  presentLegalBodyHtml,
  visibleLegalDefinitionKeys,
  resolveBindingForRole,
  type LegalImprintFieldKey,
} from '@web-studio/shared-schemas';
import { EditableContent } from '../../../edit/EditableContent';
import { pickBlockField } from '../../../lib/localized';
import { resolveLegalPageHeading } from '../../../lib/legalPageTitle';
import { pickImprintFieldLabel } from '../../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../../types';

function looksLikeBindingToken(value: string): boolean {
  return /^@[a-zA-Z][a-zA-Z0-9.-]*$/.test(value.trim());
}

export function LegalImprintBlock({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
}: BlockRenderProps) {
  const target = isActive ? activeEditTarget : null;
  const HeadingTag = headingLevel;
  const bodyHtml = pickBlockField(block.content, LEGAL_BODY_HTML_FIELD_KEY, locale);
  const proseHtml = presentLegalBodyHtml(bodyHtml);
  const fieldKeys = visibleLegalDefinitionKeys('impressum', proseHtml);
  const pageHeading = resolveLegalPageHeading('impressum', locale, pages);

  return (
    <section
      className={['ws-legal', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
    >
      <div className="ws-legal__inner">
        <HeadingTag className="ws-legal__title">{pageHeading}</HeadingTag>
        {fieldKeys.length > 0 ? (
        <dl className="ws-legal__list">
          {fieldKeys.map((fieldKey) => {
            const labelKey = legalFieldLabelContentKey(fieldKey);
            const labelOverride = pickBlockField(block.content, labelKey, locale);
            const label = pickImprintFieldLabel(fieldKey as LegalImprintFieldKey, locale, labelOverride);
            const rawValue = pickBlockField(block.content, fieldKey, locale);
            const bound = isFieldRoleBound(block.fieldBindings, fieldKey);
            const bindingRef = resolveBindingForRole(block.fieldBindings, fieldKey);
            const bindingToken = bindingRef
              ? bindingRefDisplayToken(bindingRef)
              : bindingPlaceholderToken(fieldKey);
            const trimmed = rawValue.trim();
            const hasRealValue =
              !isPdlMissingValue(trimmed) && !looksLikeBindingToken(trimmed);
            const showBindingToken = Boolean(editable && bound && !hasRealValue);
            const value = hasRealValue
              ? rawValue
              : editable
                ? bound
                  ? bindingToken
                  : '\u00a0'
                : '';
            if (!value && !editable) {
              return null;
            }
            return (
              <div key={fieldKey} className="ws-legal__row">
                <dt className="ws-legal__term">
                  <EditableContent
                    fieldKey={labelKey}
                    editable={editable}
                    activeEditTarget={target}
                  >
                    {label}
                  </EditableContent>
                </dt>
                <dd
                  className={[
                    'ws-legal__value',
                    showBindingToken ? 'ws-legal__value--binding-token' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <EditableContent
                    fieldKey={fieldKey}
                    editable={editable}
                    activeEditTarget={target}
                  >
                    {value}
                  </EditableContent>
                </dd>
              </div>
            );
          })}
        </dl>
        ) : null}
        {proseHtml ? (
          <div className="ws-legal__prose" dangerouslySetInnerHTML={{ __html: proseHtml }} />
        ) : null}
      </div>
    </section>
  );
}
